import { useMemo } from "react"
import {
  ConditionGroup,
  Meeting,
  MeetingAttachableType,
  MuxVideoTokenResponse,
  NextPageParams,
  PaginationMetadata,
} from "@/schema"
import { useDataTableStore } from "@/stores/data-table-store"
import { useInfiniteQuery, useQuery, UseQueryOptions } from "@tanstack/react-query"

import { isMeetingPromptInProgress } from "@/lib/prompt-status"
import { TasksResponse } from "@/hooks/api/tasks/api"
import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"

import {
  downloadMeetingTranscript,
  fetchBusinessMeetings,
  fetchContactMeetings,
  fetchHouseholdMeetings,
  fetchMeeting,
  fetchMeetingAudioDownloadUrl,
  fetchMeetingPreview,
  fetchMeetingPrivacy,
  fetchMeetings,
  fetchMeetingsList,
  fetchMeetingTasks,
  fetchMeetingVideoDownloadUrl,
  fetchMuxVideoToken,
  MeetingsListResponse,
  MeetingsResponse,
} from "./api"

// Meeting queries
export function useFetchMeetings({
  startDate,
  endDate,
  scope,
  limit,
  userIds,
  feed,
  conditionGroup,
  search,
  enabled = true,
}: {
  startDate?: Date
  endDate?: Date
  scope?: "upcoming_or_current" | "recently_completed" | "today" | "processed"
  limit?: number
  userIds?: string[]
  feed?: boolean
  conditionGroup?: ConditionGroup
  search?: string
  enabled?: boolean
}) {
  const getContext = useApiContext()
  const bookId = useBookId()
  return useQuery<{ meetings: Meeting[] }, Error>({
    queryKey: [
      "meetings",
      bookId,
      scope,
      startDate?.toISOString().split("T")[0],
      endDate?.toISOString().split("T")[0],
      limit,
      userIds,
      conditionGroup,
      search,
    ],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchMeetings(context, { startDate, endDate, scope, limit, userIds, feed, conditionGroup, search })
    },
    enabled,
  })
}

export function useInfiniteMeetingFeed({
  startDate,
  endDate,
  scope,
  userIds,
  conditionGroup,
  search,
  enabled = true,
}: {
  startDate: Date
  endDate: Date
  scope?: "processed"
  userIds?: string[]
  conditionGroup?: ConditionGroup
  search?: string
  enabled?: boolean
}) {
  const getContext = useApiContext()
  const bookId = useBookId()

  return useInfiniteQuery<
    MeetingsResponse,
    Error,
    { meetings: Meeting[]; pagination?: PaginationMetadata },
    readonly unknown[],
    NextPageParams | undefined
  >({
    queryKey: [
      "meetings",
      "feed",
      bookId,
      scope,
      startDate.toISOString().split("T")[0],
      endDate.toISOString().split("T")[0],
      userIds,
      conditionGroup,
      search,
    ],
    queryFn: async ({ pageParam }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      return fetchMeetings(context, {
        startDate,
        endDate,
        scope,
        userIds,
        feed: true,
        pageParams: pageParam,
        conditionGroup,
        search,
      })
    },
    initialPageParam: undefined,
    enabled,
    getNextPageParam: (lastPage) => lastPage.pagination?.next_page_params ?? undefined,
    select: (data) => ({
      meetings: data.pages.flatMap((page) => page.meetings),
      pagination: data.pages[data.pages.length - 1]?.pagination,
    }),
  })
}

export function useFetchTodaysMeetings({
  limit,
  conditionGroup,
  enabled = true,
}: { limit?: number; conditionGroup?: ConditionGroup; enabled?: boolean } = {}) {
  const getContext = useApiContext()
  const bookId = useBookId()

  return useQuery<{ meetings: Meeting[] }, Error>({
    queryKey: ["meetings", "today", bookId, limit, conditionGroup],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchMeetings(context, { scope: "today", limit, conditionGroup })
    },
    enabled,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  })
}

export function useHouseholdMeetings(householdId: string) {
  const getContext = useApiContext()

  return useQuery({
    queryKey: ["meetings", "household", householdId],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchHouseholdMeetings(context, householdId)
    },
    enabled: !!householdId,
  })
}

export function useMeetingPreview(type: MeetingAttachableType, id: string, bookIdOverride?: string) {
  const getContext = useApiContext(bookIdOverride)
  const currentBookId = useBookId()

  return useQuery({
    queryKey: ["meetings", type, id, "preview", bookIdOverride ?? currentBookId],
    queryFn: async () => {
      const context = await getContext()
      return fetchMeetingPreview(context, type, id)
    },
    enabled: !!id,
    staleTime: 60_000,
  })
}

export function useHouseholdPastMeetings(householdId: string, enabled: boolean = true) {
  const getContext = useApiContext()
  const twentyFourMonthsAgo = new Date()
  twentyFourMonthsAgo.setMonth(twentyFourMonthsAgo.getMonth() - 24)
  const sinceDate = twentyFourMonthsAgo.toISOString().split("T")[0]

  return useQuery({
    queryKey: ["meetings", "household", householdId, "past", sinceDate],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      const response = await fetchHouseholdMeetings(context, householdId, { scope: "past", since: sinceDate })
      return {
        meetings: response.meetings.sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime()),
      }
    },
    enabled: !!householdId && enabled,
  })
}

export function useHouseholdUpcomingMeetings(householdId: string, enabled: boolean = true) {
  const getContext = useApiContext()

  return useQuery({
    queryKey: ["meetings", "household", householdId, "future"],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      const response = await fetchHouseholdMeetings(context, householdId, { scope: "future" })
      return {
        meetings: response.meetings.sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime()),
      }
    },
    enabled: !!householdId && enabled,
  })
}

export function useContactMeetings(contactId: string) {
  const getContext = useApiContext()

  return useQuery({
    queryKey: ["meetings", "contact", contactId],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchContactMeetings(context, contactId)
    },
    enabled: !!contactId,
  })
}

export function useBusinessMeetings(businessId: string) {
  const getContext = useApiContext()

  return useQuery({
    queryKey: ["meetings", "business", businessId],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchBusinessMeetings(context, businessId)
    },
    enabled: !!businessId,
  })
}

export function useAttachableMeetings({ type, id }: { type: MeetingAttachableType; id: string }) {
  const householdQuery = useHouseholdMeetings(type === "household" ? id : "")
  const contactQuery = useContactMeetings(type === "contact" ? id : "")
  const businessQuery = useBusinessMeetings(type === "business" ? id : "")

  if (type === "household") return householdQuery
  if (type === "contact") return contactQuery
  return businessQuery
}

export function useMeeting(meetingId: string, options?: Omit<UseQueryOptions<Meeting, Error>, "queryKey" | "queryFn">) {
  const getContext = useApiContext()
  return useQuery<Meeting, Error>({
    queryKey: ["meeting", meetingId],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchMeeting(context, meetingId)
    },
    enabled: !!meetingId,
    refetchInterval: (query) => {
      const m = query.state.data
      if (!m) return false

      return m.is_processing_recording ||
        isMeetingPromptInProgress(m, "Prompt::MeetingQuickSummary") ||
        isMeetingPromptInProgress(m, "Prompt::MeetingNote") ||
        isMeetingPromptInProgress(m, "Prompt::MeetingTasks")
        ? 3000
        : false
    },
    ...options,
  })
}

export function useMeetingPrivacy(meetingId: string, enabled: boolean) {
  const getContext = useApiContext()

  return useQuery({
    queryKey: ["meetingPrivacy", meetingId],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchMeetingPrivacy(context, meetingId)
    },
    enabled: !!meetingId && enabled,
  })
}

export function useMeetingMuxVideoToken(
  meetingId: string,
  options?: Omit<UseQueryOptions<MuxVideoTokenResponse, Error>, "queryKey" | "queryFn">
) {
  const getContext = useApiContext()
  return useQuery<MuxVideoTokenResponse, Error>({
    queryKey: ["meetingMuxVideoToken", meetingId],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchMuxVideoToken(context, meetingId)
    },
    enabled: !!meetingId,
    ...options,
  })
}

export function useMeetingAudioDownloadUrl(
  meetingId: string,
  options?: Omit<UseQueryOptions<{ download_url: string }, Error>, "queryKey" | "queryFn">
) {
  const getContext = useApiContext()
  return useQuery<{ download_url: string }, Error>({
    queryKey: ["meetingAudioDownloadUrl", meetingId],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchMeetingAudioDownloadUrl(context, meetingId)
    },
    enabled: !!meetingId,
    ...options,
  })
}

export function useMeetingTasks(
  meetingId: string,
  options?: Omit<UseQueryOptions<TasksResponse, Error>, "queryKey" | "queryFn">
) {
  const getContext = useApiContext()
  return useQuery<TasksResponse, Error>({
    queryKey: ["meeting", meetingId, "tasks"],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchMeetingTasks(context, meetingId)
    },
    enabled: !!meetingId,
    ...options,
  })
}

export function useNextMeeting() {
  const getContext = useApiContext()

  return useQuery({
    queryKey: ["nextMeeting"],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      const response = await fetchMeetings(context, { scope: "upcoming_or_current", limit: 1 })

      const meeting = response.meetings?.[0]

      if (meeting) {
        return meeting
      }

      return null
    },
    refetchInterval: 30000,
    refetchIntervalInBackground: false,
  })
}

export function useDownloadMeetingTranscript() {
  const getContext = useApiContext()

  return async (meetingId: string, meetingTitle?: string) => {
    const context = await getContext()
    if (!context) throw new Error("No context found")

    const blob = await downloadMeetingTranscript(context, meetingId)

    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `${meetingTitle || "meeting"}-transcript.docx`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }
}

export function useDownloadMeetingVideo() {
  const getContext = useApiContext()

  return async (meetingId: string) => {
    const context = await getContext()
    if (!context) throw new Error("No context found")

    const { download_url } = await fetchMeetingVideoDownloadUrl(context, meetingId)

    const a = document.createElement("a")
    a.href = download_url
    a.style.display = "none"
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }
}

export function useDownloadMeetingAudio() {
  const getContext = useApiContext()

  return async (meetingId: string) => {
    const context = await getContext()
    if (!context) throw new Error("No context found")

    const { download_url } = await fetchMeetingAudioDownloadUrl(context, meetingId)

    const a = document.createElement("a")
    a.href = download_url
    a.style.display = "none"
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }
}

export function useInfiniteMeetings(enabled?: boolean) {
  const getContext = useApiContext()
  const { searchTerm, completedConditions, viewSort } = useDataTableStore()

  const conditionGroup = useMemo(
    () =>
      ({
        combinator: "and",
        conditions: completedConditions,
      }) as ConditionGroup,
    [completedConditions]
  )

  return useInfiniteQuery<
    MeetingsListResponse,
    Error,
    { meetings: Meeting[]; pagination: PaginationMetadata },
    (string | ConditionGroup | undefined | { field?: string; direction?: "asc" | "desc" })[],
    NextPageParams | undefined
  >({
    queryKey: ["meetings", "infinite", searchTerm, conditionGroup, viewSort],
    staleTime: 0,
    queryFn: async ({ pageParam }) => {
      const content = await getContext()
      if (!content) throw new Error("No content found")

      return fetchMeetingsList(content, conditionGroup, pageParam, searchTerm, viewSort)
    },
    initialPageParam: undefined,
    enabled,
    getNextPageParam: (lastPage) => lastPage.pagination?.next_page_params ?? undefined,
    select: (data) => ({
      meetings: data.pages.flatMap((page) => page.meetings),
      pagination: data.pages[data.pages.length - 1]?.pagination,
    }),
  })
}
